import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Grid, CircularProgress, Typography, Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Stack, Drawer,
  Chip,
  TextField,
  Divider,
  ButtonGroup,
  MenuItem,
  Menu,
  Grow,
  Paper,
  Popper,
  MenuList,
  ClickAwayListener,
  Tabs,
  Tab,

} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Add, BarChart, ArrowDropDown } from "@mui/icons-material";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";

import { ProducerCommissionCompanyOverride } from "../../interfaces/DTO/commissions/producerCommission";
import { MimshakData } from "../../interfaces/DTO/mimshakData";
import { producerCommissionOverrideService } from "../../services/commissions/ProducerCommissionOverrideService";
import { useAuthStore } from "../../stores/authStore";
import { useAgentProducerCodes } from "../../hooks/agent/useAgentProducerCodes";
import FeesByProductDnD from "./FeesByProductDnD";
import MimshakPieChart from "../PersonFinancialData/Charts/MimshakPieChart";
import WeightedPerformanceBar from "../PersonFinancialData/Charts/WeightedPerformanceBar";
import TransferFlowDiagram from "../PersonFinancialData/diagrams/TransferFlowDiagram";

import PathSelection from "./PathSelection";
import { MimshakProductUtil } from "../../services/MimshakProductUtil";
import { MimshakDataUtil } from "../../services/mimshakDataUtil";
import { MimshakService } from "../../services/mimshakService";
import { FundDataBasic } from "../../interfaces/DTO/FundDataBasic";
import { replacePathsInProduct, mergeDuplicateInvestmentPathsByPathCode, resetOriginalInvestmentPaths, PathFundSelection, applyFundsSumToMimshak } from "../../hooks/feeLab/useNewProductHandlers";
import { fundService } from "../../services/fundService";

import { HandlerAutocompleteField } from "../../shared/Autocompletes/HandlerAutocompleteField";
import CurrencyTextField from "../../shared/UIParts/InputFields/CurrencyTextField";
import CommissionOverridesChart from "../Commission/CommissionOverridesChart";
import { cloneDeep } from "lodash";
import { AdjustedProductType, ProductType } from "../../interfaces/DTO/mislaka_xml_models";
import { formatNumberFloor } from "../../utils/namesFormater";
import ExposureGaugesByMimshakDataInteractive from "../PersonFinancialData/ExposureGaugesByMimshakDataInteractive";
import CommissionComparisonChart from "../Commission/CommissionComparisonChart";
import NewProductTypeDrawer from "./NewProductTypeDrawer";
import { getAdditionTypeColor, getAdditionTypeLabel } from "../../utils/labUtils";
import { colorProductType, colorProductTypeUniversal } from "../../utils/colorEnumFormatter";
import { useNewProductHandlers } from "../../hooks/feeLab/useNewProductHandlers";
import FeeSavingsComparison from "./FeeSavingsComparison";
import { Simulation, SimulationMimshakData, FundSumDropMap, PolicyFundAdditions, FundAdditionType, FundSumChip } from "../../interfaces/DTO/simulation";
import SimulationBar from "./Simulation/SimulationBar";

import { Contact } from "../../interfaces/DTO/crm/contact";
import { simulationMimshakDataService } from "../../services/SimulationMimshakDataService";
import { SimulationChangeDetectionUtil } from "../../services/userTask/simulationChangeDetectionUtil";
import { ChangesDataUtils } from "./Reports/ChangesDataUtils";
import { SimulationChangeType } from "../../interfaces/DTO/simulation";
import SummaryChangesVisual from "./SummaryChangesVisual";
import WeightedProductYieldsSummary from "../PersonFinancialData/Cards/AverageFundYieldsSummary";
import PathRatingCubes from "./PathRatingCubes";
import SelectNewPathDrawer from "./SelectNewPathDrawer";
import { usePathDragChanges, NewExposureMap } from "../../hooks/feeLab/usePathDragChanges";
import ExposureLabDnD from "./ExposureLabDnD";
import YieldLab from "./YieldLab";

import { lazy, Suspense } from "react";
import SimulationComparisonDialog from "./Simulation/SimulationComparisonDialog";
import {
  useContactPreferencesLastByContactId,
  useFamilyPreferencesLastByContactId,
  usePartialUpdateContactPreferences,
  useCreateContactPreferences
} from "../../hooks/useContactPreferences";
import { ContactPreferenceType, PreferenceDistributionData, ContactPreferences } from "../../interfaces/DTO/contactPreferences";
import { createContactColorMap } from "../../utils/contactColorMapping";
import AssetChipsPanel from "./AssetChipsPanel";
const FillInvestmentProvidentFundDrawer = lazy(() => import("./FillInvestmentProvidentFundDrawer"));

const FeesLabPDFExport = lazy(() => import("./Reports/FeesLabPDFExport").then(m => ({ default: m.FeesLabPDFExport })));
const WordReportPreviewDialog = lazy(() => import("./Reports/WordReportPreviewDialog").then(m => ({ default: m.WordReportPreviewDialog })));
const FeesLabWYSIWYGLauncher = lazy(() => import('./Reports/FeesLabWYSIWYGLauncher').then(m => ({ default: m.FeesLabWYSIWYGLauncher })));
const DiffDrawer = lazy(() => import("./DiffDrawer"));

interface Props {
  data: MimshakData[];
  selectedPeriodType: "REGULAR" | "PRE_ADVICE";
  id:number;
  contacts: Contact[];
  familyId?: number;
  selectedPeriod: string;
}

const FeesLabPageDnD: React.FC<Props> = ({ data,selectedPeriodType,id, contacts,familyId, selectedPeriod }) => {
  const user = useAuthStore((state) => state.user);
  const userId = user?.id;
  const queryClient = useQueryClient();

  // Create contact color mapping when there are multiple contacts
  const contactColorMap = useMemo(() => {
    if (contacts.length <= 1) return {};
    const contactIds = contacts.map(c => c.id);
    return createContactColorMap(contactIds);
  }, [contacts]);

const [newProductDrawer, setNewProductDrawer] = useState<MimshakData | null>(null);
const [comparisonSimulation, setComparisonSimulation] = useState<{
  simulation: Simulation;
  filteredData: SimulationMimshakData[];
} | null>(null);
const [simulationDrawerOpen, setSimulationDrawerOpen] = useState(false);
const [selectedSimulation, setSelectedSimulation] = useState<Simulation | null>(null);
const [loadedNotes, setLoadedNotes] = useState<Record<string, string>>({});
const [excludedSimulationItems, setExcludedSimulationItems] = useState<SimulationMimshakData[]>([]);
const [excludedModifiedRows, setExcludedModifiedRows] = useState<MimshakData[]>([]);
const [editedFields, setEditedFields] = useState<Record<string, Partial<SimulationMimshakData>>>({});
  const { data: agentProducerCodes = [], isLoading: loadingAgentCodes } = useAgentProducerCodes();

  // Fetch preferences - use family preferences if familyId exists, otherwise contact preferences
  const { data: contactPrefs } = useContactPreferencesLastByContactId(
    familyId ? 0 : id,
    { enabled: !familyId }
  );
  const { data: familyPrefs } = useFamilyPreferencesLastByContactId(
    familyId || 0,
    { enabled: !!familyId }
  );
  const updatePreference = usePartialUpdateContactPreferences();
  const createPreference = useCreateContactPreferences();

  const [overrides, setOverrides] = useState<ProducerCommissionCompanyOverride[] | null>(null);
  const [loading, setLoading] = useState(true);
 const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
 const [wordDialogOpen, setWordDialogOpen] = useState(false);
 const [htmlDialogOpen, setHtmlDialogOpen] = useState(false);
 const [transferFlowDrawerOpen, setTransferFlowDrawerOpen] = useState(false);
  const [insightsTab, setInsightsTab] = useState(0);
  const [originalRows, setOriginalRows] = useState<MimshakData[]>([]);

  // Path drag changes hook
  const { dragChanges, newExposureMap, addDragChange, clearDragChanges, removePolicyChanges, loadExposureMap } = usePathDragChanges();

  // Contact preferences state
  const [capitalPreferenceData, setPreferenceData] = useState<PreferenceDistributionData>({
    stockExposure: [],
    currencyExposure: [],
    foreignExposure: []
  });
  const [currentPreferenceId, setCurrentPreferenceId] = useState<number | null>(null);

  // Education fund preferences state
  const [educationFundPreferenceData, setEducationFundPreferenceData] = useState<PreferenceDistributionData>({
    stockExposure: [],
    currencyExposure: [],
    foreignExposure: []
  });

  // Pension preferences state
  const [pensionPreferenceData, setPensionPreferenceData] = useState<PreferenceDistributionData>({
    stockExposure: [],
    currencyExposure: [],
    foreignExposure: []
  });
  const [modifiedRows, setModifiedRows] = useState<MimshakData[]>([]);
  const [newFeesMap, setNewFeesMap] = useState<Record<string, number | null>>({});
  const [loadedManualFees, setLoadedManualFees] = useState<Record<string, { fee: number; handlerCode: string }>>({});
  const [showDiff, setShowDiff] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scatterDrawerPolicyNumber, setScatterDrawerPolicyNumber] = useState<string | null>(null);
  const validAdjustedProductTypes: AdjustedProductType[] = [
    AdjustedProductType.EDUCATION_FUND,
    AdjustedProductType.PURE_SAVINGS_POLICY,
    AdjustedProductType.INVESTMENT_PROVIDENT_FUND,
    AdjustedProductType.CAPITAL_PROVIDENT_FUND,
    AdjustedProductType.COMBINED_PROVIDENT_FUND,
    AdjustedProductType.PENSION_PROVIDENT_FUND,
    AdjustedProductType.PROVIDENT_FUND,
  ];

  const validProductTypes: ProductType[] = [
    ProductType.EDUCATION_FUND,
    ProductType.PROVIDENT_FUND,
    ProductType.INVESTMENT_PROVIDENT_FUND,
  ];
    const [newFundSum, setNewFundSum] = useState<number | null>(null);
  const [fundSumChips, setFundSumChips] = useState<FundSumChip[]>([]);
  const [fundSumDropMap, setFundSumDropMap] = useState<FundSumDropMap>({});
  const [selectedAdditionType, setSelectedAdditionType] = useState<FundAdditionType>('deposit');
  const [additionMenuAnchor, setAdditionMenuAnchor] = useState<null | HTMLElement>(null);
  const [newHandlerCode, setNewHandlerCode] = useState<string>("");
  const [manualAssetChips, setManualAssetChips] = useState<MimshakData[]>([]);
  const [fillProvidentFundDrawerOpen, setFillProvidentFundDrawerOpen] = useState(false);
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState<null | HTMLElement>(null);
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [executiveDialogOpen, setExecutiveDialogOpen] = useState(false);
  const [didInitialize, setDidInitialize] = useState(false)
  const [handlersSet, setHandlersSet] = useState<Set<string>>(new Set());
  const [sortOrderMap, setSortOrderMap] = useState<Record<string, number>>({});

// חזרה לגרסה המקורית - groupedWithNew
const groupedWithNew = useMemo(() => {
  const originalMap = new Map(originalRows.map((r) => [r.policy_number, r]));
  const resultMap: Record<string, MimshakData[]> = {};

  // נבנה קבוצה לפי ה-handler_code של modifiedRows
  // נכניס את האובייקט המקורי אם קיים (כדי לשמור handler_code מקורי)
  modifiedRows.forEach((modified) => {
    const original = originalMap.get(modified.policy_number);
    const key = modified.handler_code;

    if (!resultMap[key]) {
      resultMap[key] = [];
    }

    // אם יש original - נכניס אותו (שומר handler_code מקורי)
    // אם אין (מוצר חדש) - נכניס את modified
    resultMap[key].push(original ?? modified);
  });

  return resultMap;
}, [originalRows, modifiedRows]);




  const [agentAssignmentMap, setAgentAssignmentMap] = useState<Record<string, "none" | "full" | "viewOnly">>({});
  
useEffect(() => {
  setDidInitialize(false);
  setSortOrderMap({});
}, [id,selectedPeriod]);
const filteredData = useMemo(() => {
  return data.filter((item) => {
    const adj = item.adjusted_product_type;
    if (adj !== undefined && adj !== null) {
      return validAdjustedProductTypes.includes(adj);
    }
    return validProductTypes.includes(item.product_type);
  });
}, [data]);

const originalProducts = useMemo(() => MimshakDataUtil.extractAllProducts(originalRows), [originalRows]);
const modifiedProducts = useMemo(() => MimshakDataUtil.extractAllProducts(modifiedRows), [modifiedRows]);

const performanceFundData = useMemo(() => {
  const codes = MimshakService.extractPathCodesFromProducts([...originalProducts, ...modifiedProducts]);
  const uniqueCodes = Array.from(new Set(codes));
  return uniqueCodes
    .map((id) => fundService.getFundFromCacheById(id))
    .filter(Boolean) as FundDataBasic[];
}, [originalProducts, modifiedProducts]);

const excludedOriginalRows = useMemo(() => {
  return data.filter((item) => {
    const adj = item.adjusted_product_type;
    if (adj !== undefined && adj !== null) {
      return !validAdjustedProductTypes.includes(adj);
    }
    return !validProductTypes.includes(item.product_type);
  });
}, [data]);


const originalEducationRows = useMemo(() => {
  return originalRows.filter(row =>
    row.product_type === ProductType.EDUCATION_FUND ||
    row.adjusted_product_type === AdjustedProductType.EDUCATION_FUND
  );
}, [originalRows]);

const modifiedEducationRows = useMemo(() => {
  return modifiedRows.filter(row =>
    row.product_type === ProductType.EDUCATION_FUND ||
    row.adjusted_product_type === AdjustedProductType.EDUCATION_FUND
  );
}, [modifiedRows]);

const hasEducationFunds = originalEducationRows.length > 0 || modifiedEducationRows.length > 0;

const FUTURE_CAPITAL_MIN_YEARS = 7;

const hasFutureAssets = useMemo(() => {
  return originalRows.some(row => row.product_type === ProductType.PROVIDENT_FUND) ||
         modifiedRows.some(row => row.product_type === ProductType.PROVIDENT_FUND);
}, [originalRows, modifiedRows]);

const originalProvidentRows = useMemo(() => originalRows.filter(r => r.product_type === ProductType.PROVIDENT_FUND), [originalRows]);
const modifiedProvidentRows = useMemo(() => modifiedRows.filter(r => r.product_type === ProductType.PROVIDENT_FUND), [modifiedRows]);
const originalCapitalRows = useMemo(() => originalRows.filter(r => r.product_type !== ProductType.PROVIDENT_FUND), [originalRows]);
const modifiedCapitalRows = useMemo(() => modifiedRows.filter(r => r.product_type !== ProductType.PROVIDENT_FUND), [modifiedRows]);

const capitalTabIndex = hasFutureAssets ? 1 : -1;
const futureAssetsTabIndex = hasFutureAssets ? 2 : -1;
const educationTabIndex = hasEducationFunds ? (hasFutureAssets ? 3 : 1) : -1;
const scatterTabIndex = (hasFutureAssets ? 3 : 1) + (hasEducationFunds ? 1 : 0);
const yieldRatingTabIndex = scatterTabIndex + 1;
const changesTabIndex = scatterTabIndex + 2;

const simulationItems = useMemo(() => {
  const items = SimulationChangeDetectionUtil.convertModifiedToSimulationItems(originalRows, modifiedRows, fundSumDropMap);
  if (Object.keys(loadedNotes).length === 0) return items;
  return items.map(item => ({
    ...item,
    note: loadedNotes[item.policy_number] ?? item.note,
  }));
}, [originalRows, modifiedRows, fundSumDropMap, loadedNotes]);

const simulationItemsWithEdits = useMemo(() => {
  if (Object.keys(editedFields).length === 0) return simulationItems;
  return simulationItems.map(item => {
    const edits = editedFields[item.policy_number];
    return edits ? { ...item, ...edits } : item;
  });
}, [simulationItems, editedFields]);

const changesCount = useMemo(() =>
  simulationItems.filter(i => !(i.change_types.length === 1 && i.change_types[0] === SimulationChangeType.UNCHANGED)).length,
  [simulationItems]
);


const handleSimulationItemUpdate = useCallback((policyNumber: string, fields: Partial<SimulationMimshakData>) => {
  setEditedFields(prev => ({
    ...prev,
    [policyNumber]: { ...(prev[policyNumber] ?? {}), ...fields },
  }));
}, []);

useEffect(() => {
  // Collect handlers from BOTH original data AND modified data
  // This ensures handlers remain visible even after drag-and-drop operations
  const handlersFromOriginal = new Set(filteredData.map(item => item.handler_code));
  const handlersFromModified = new Set(modifiedRows.map(item => item.handler_code));

  // Merge both sets to include all handlers (original + moved/new ones)
  const allHandlers = new Set([
    ...handlersFromOriginal,
    ...handlersFromModified
  ]);

  setHandlersSet(allHandlers);
}, [filteredData, modifiedRows]); // Update when either data source changes

// Load preference data when fetched
useEffect(() => {
  const prefs = familyId ? familyPrefs : contactPrefs;
  if (!prefs || prefs.length === 0) {
    // No preferences found - keep empty state
    setPreferenceData({
      stockExposure: [],
      currencyExposure: [],
      foreignExposure: []
    });
    setEducationFundPreferenceData({
      stockExposure: [],
      currencyExposure: [],
      foreignExposure: []
    });
    setCurrentPreferenceId(null);
    setPensionPreferenceData({ stockExposure: [], currencyExposure: [], foreignExposure: [] });
    return;
  }

  // Find PENSION_DISTRIBUTION preference
  const pensionPref = prefs.find(p => p.preference_type === ContactPreferenceType.PENSION_DISTRIBUTION);
  if (pensionPref && pensionPref.data) {
    const data = pensionPref.data as PreferenceDistributionData;
    setPensionPreferenceData({
      stockExposure: data.stockExposure || [],
      currencyExposure: data.currencyExposure || [],
      foreignExposure: data.foreignExposure || []
    });
  }

  // Find CAPITAL_DISTRIBUTION preference
  const capitalPref = prefs.find(p => p.preference_type === ContactPreferenceType.CAPITAL_DISTRIBUTION);
  if (capitalPref && capitalPref.data) {
    const data = capitalPref.data as PreferenceDistributionData;
    setPreferenceData({
      stockExposure: data.stockExposure || [],
      currencyExposure: data.currencyExposure || [],
      foreignExposure: data.foreignExposure || []
    });
    setCurrentPreferenceId(capitalPref.id);
  }

  // Find EDUCATION_FUND_DISTRIBUTION preference
  const educationPref = prefs.find(p => p.preference_type === ContactPreferenceType.EDUCATION_FUND_DISTRIBUTION);
  if (educationPref && educationPref.data) {
    const data = educationPref.data as PreferenceDistributionData;
    setEducationFundPreferenceData({
      stockExposure: data.stockExposure || [],
      currencyExposure: data.currencyExposure || [],
      foreignExposure: data.foreignExposure || []
    });
  }
}, [contactPrefs, familyPrefs, familyId]);

// useEffect(() => {
//   setHandlersSet(prev => {
//     const next = new Set(prev);
//     Object.keys(grouped).forEach(k => next.add(k)); // מוסיף קיימים
//     return next;
//   });
// }, [grouped]); // רץ כשיש handlers חדשים/טעינה
// // רינדור לפי הסדר ב-Set



useEffect(() => {
  const load = async () => {
    if (!userId || didInitialize) return;

    const providentOverrides = await producerCommissionOverrideService.fetchByUserId(userId, ProductType.PROVIDENT_FUND);

    setOriginalRows(filteredData);
    // Clone and reset deposit/withdrawal fields
    // אנחנו מאפסים deposit ו-withdrawal כי אלו הפקדות ומשיכות שכבר התבצעו בעבר (היסטוריה)
    // ולא רלוונטיות לסימולציה החדשה. הפקדות חדשות יתווספו דרך drag & drop
    const clonedData = cloneDeep(filteredData).map((row) => ({
      ...row,
      deposit_amount: 0,
      withdrawal_amount: 0,
      recurrent_deposit: 0,
      transfer_in: null as number | null,
      transfer_out: null as number | null,
      transfer_out_to: null as string | null,
      agent_code: null as string | null,
    }));
    const initialSortOrder: Record<string, number> = {};
    clonedData.forEach((row, i) => { initialSortOrder[row.policy_number] = i * 1000; });
    setSortOrderMap(initialSortOrder);
    setModifiedRows(clonedData);
    setDidInitialize(true);
    setOverrides(providentOverrides);
  
    setLoading(false);
  };

  load();
}, [userId, filteredData, didInitialize]);


const { isModified, isModifiedSavingFee } = useMemo(() => {
  const originalMap = new Map(originalRows.map((r) => [r.policy_number, r]));

  let modified = false;
  let modifiedSavingFee = false;

  for (const r of modifiedRows) {
    const original = originalMap.get(r.policy_number);
    if (!original) {
      modified = true;
      modifiedSavingFee = true;
      continue;
    }

    if (original.handler_code !== r.handler_code) {
      modified = true;
       modifiedSavingFee = true;
    }

    const originalFundIds = original.fund_ids || [];
    const currentFundIds = r.fund_ids || [];
    const sameFundIds = originalFundIds.length === currentFundIds.length &&
      originalFundIds.every((id, index) => id === currentFundIds[index]);
    if (!sameFundIds) {
      modified = true;
    }

    const additions = fundSumDropMap?.[r.policy_number];
    if (additions && Object.values(additions).some(v => v)) {
      modified = true;
    }

    if (original.saving_fee !== r.saving_fee) {
      modifiedSavingFee = true;
    }

    if (modified && modifiedSavingFee) break;
  }
  
  return { isModified: modified, isModifiedSavingFee: modifiedSavingFee };
}, [modifiedRows, originalRows, fundSumDropMap]);
// פונקציה חדשה להשוואה
const handleCompareSimulation = async (sim: Simulation) => {
  try {
    const items = await simulationMimshakDataService.getBySimulationId(sim.id);
    const filtered = items.filter(item => {
      const adj = item.adjusted_product_type;
      if (adj !== undefined && adj !== null) {
        return validAdjustedProductTypes.includes(adj);
      }
      return validProductTypes.includes(item.product_type as ProductType);
    });
    
    setComparisonSimulation({ simulation: sim, filteredData: filtered });
  } catch (err) {
    console.error("❌ שגיאה בטעינת סימולציה להשוואה:", err);
    alert("טעינת הסימולציה להשוואה נכשלה");
  }
};

const handleLoadSimulationItems = async (sim: Simulation) => {
  try {
    const items = await simulationMimshakDataService.getBySimulationId(sim.id);
      const filtered = items.filter(item => {
      const adj = item.adjusted_product_type;
      if (adj !== undefined && adj !== null) {
        return validAdjustedProductTypes.includes(adj);
      }
      return validProductTypes.includes(item.product_type as ProductType);
    });
    const excluded = items.filter(item => {
      const adj = item.adjusted_product_type;
      if (adj !== undefined && adj !== null) {
        return !validAdjustedProductTypes.includes(adj);
      }
      return !validProductTypes.includes(item.product_type as ProductType);
    });
    const noteMap: Record<string, string> = {};
    items.forEach(item => {
      if (item.note) noteMap[item.policy_number] = item.note;
    });
    const restoredExposureMap: NewExposureMap = (sim.data?.exposure_map as NewExposureMap) ?? {};
    const restoredFundSumDropMap: FundSumDropMap = {};
    filtered.forEach(item => {
      const additions: PolicyFundAdditions = {};
      const changeTypes = item.change_types ?? [];
      const isNewPolicy = item.new_policy;
      if (changeTypes.includes(SimulationChangeType.DEPOSIT) && item.deposit_amount)
        additions.deposit = item.deposit_amount;
      if (isNewPolicy && changeTypes.includes(SimulationChangeType.OPEN_NEW) && item.deposit_amount)
        additions.deposit = item.deposit_amount;
      if (changeTypes.includes(SimulationChangeType.STOP_RECURRENT_DEPOSIT))
        additions.recurrent = null;
      else if ((changeTypes.includes(SimulationChangeType.NEW_RECURRENT_DEPOSIT) || changeTypes.includes(SimulationChangeType.CHANGE_RECURRENT_DEPOSIT) || changeTypes.includes(SimulationChangeType.OPEN_NEW)) && item.recurrent_deposit)
        additions.recurrent = item.recurrent_deposit;
      if (item.transfer_out)
        additions.transfer = item.transfer_out;
      if (changeTypes.includes(SimulationChangeType.WITHDRAWAL) && item.withdrawal_amount)
        additions.withdrawal = item.withdrawal_amount;
      if (Object.keys(additions).length > 0) {
        restoredFundSumDropMap[item.policy_number] = additions;
      }
      // Restore _baseFundsSum for new products so chip removal returns to the base amount.
      // funds_sum in the simulation already includes chips, so subtract them back out.
      if (isNewPolicy) {
        const chipTotal = (additions.deposit ?? 0) + (additions.recurrent ?? 0) + (additions.transfer ?? 0) - (additions.withdrawal ?? 0);
        (item as MimshakData & { _baseFundsSum?: number })._baseFundsSum = (item.funds_sum ?? 0) - chipTotal;
      }
    });

    const restoredManualFees: Record<string, { fee: number; handlerCode: string }> = {};
    filtered.forEach(item => {
      const hasChange = item.change_types?.some(ct => ct !== SimulationChangeType.UNCHANGED);
      if (hasChange && item.saving_fee != null) {
        restoredManualFees[item.policy_number] = { fee: item.saving_fee, handlerCode: item.handler_code };
      }
    });

    setLoadedNotes(noteMap);
    setEditedFields({});
    setSelectedSimulation(sim);
    setModifiedRows(filtered);
    setExcludedSimulationItems(excluded);
    setExcludedModifiedRows(excluded);
    setFundSumDropMap(restoredFundSumDropMap);
    setLoadedManualFees(restoredManualFees);
    loadExposureMap(restoredExposureMap);

  } catch (err) {
    console.error("❌ שגיאה בטעינת סימולציה:", err);
    alert("טעינת הסימולציה נכשלה");
  }
};
const handleExitSimulationMode = () => {
  setSelectedSimulation(null);
  setLoadedNotes({});
  setEditedFields({});
  setLoadedManualFees({});
  setExcludedSimulationItems([]);
  setExcludedModifiedRows([]);
  // Clone and reset deposit/withdrawal fields
  // אנחנו מאפסים deposit ו-withdrawal כי אלו הפקדות שכבר בוצעו (היסטוריה)
  // הפקדות חדשות לסימולציה יתווספו דרך drag & drop
  const clonedData = cloneDeep(originalRows).map(row => ({
    ...row,
    deposit_amount: 0,
    withdrawal_amount: 0,
    recurrent_deposit: null as number | null,
    transfer_in: null as number | null,
    transfer_out: null as number | null,
    transfer_out_to: null as string | null,
    agent_code: null as string | null,
  }));
  setModifiedRows(clonedData);

};

// Handler for preference changes (capital distribution)
const handlePreferenceChange = async (newData: PreferenceDistributionData) => {
  setPreferenceData(newData);

  try {
    if (currentPreferenceId) {
      // Update existing preference
      await updatePreference.mutateAsync({
        id: currentPreferenceId,
        fields: {
          data: newData,
          reported_period: selectedPeriod
        }
      });

      // Invalidate the LAST cache to force refetch
      const contactIdToInvalidate = familyId || id;
      if (familyId) {
        queryClient.invalidateQueries({ queryKey: ["contactPreferences", "familyLast", contactIdToInvalidate] });
      } else {
        queryClient.invalidateQueries({ queryKey: ["contactPreferences", "last", contactIdToInvalidate] });
      }
    } else {
      // Create new preference
      const created = await createPreference.mutateAsync(
        familyId
          ? {
              family_id: familyId,
              contact_id: null,
              preference_type: ContactPreferenceType.CAPITAL_DISTRIBUTION,
              is_family: true,
              reported_period: selectedPeriod,
              data: newData
            }
          : {
              contact_id: id,
              family_id: null,
              preference_type: ContactPreferenceType.CAPITAL_DISTRIBUTION,
              is_family: false,
              reported_period: selectedPeriod,
              data: newData
            }
      );
      setCurrentPreferenceId(created.id);

      // Invalidate the LAST cache after creation
      const contactIdToInvalidate = familyId || id;
      if (familyId) {
        queryClient.invalidateQueries({ queryKey: ["contactPreferences", "familyLast", contactIdToInvalidate] });
      } else {
        queryClient.invalidateQueries({ queryKey: ["contactPreferences", "last", contactIdToInvalidate] });
      }
    }
  } catch (error) {
    console.error("Error saving preference:", error);
  }
};

// Handler for education fund preference changes
const handleEducationPreferenceChange = async (newData: PreferenceDistributionData) => {
  setEducationFundPreferenceData(newData);

  try {
    // Find existing education fund preference
    const prefs = familyId ? familyPrefs : contactPrefs;
    const existingPref = prefs?.find(p => p.preference_type === ContactPreferenceType.EDUCATION_FUND_DISTRIBUTION);

    if (existingPref) {
      // Update existing preference
      await updatePreference.mutateAsync({
        id: existingPref.id,
        fields: {
          data: newData,
          reported_period: selectedPeriod
        }
      });
    } else {
      // Create new preference
      await createPreference.mutateAsync(
        familyId
          ? {
              family_id: familyId,
              contact_id: null,
              preference_type: ContactPreferenceType.EDUCATION_FUND_DISTRIBUTION,
              is_family: true,
              reported_period: selectedPeriod,
              data: newData
            }
          : {
              contact_id: id,
              family_id: null,
              preference_type: ContactPreferenceType.EDUCATION_FUND_DISTRIBUTION,
              is_family: false,
              reported_period: selectedPeriod,
              data: newData
            }
      );
    }

    // Invalidate the LAST cache to force refetch
    const contactIdToInvalidate = familyId || id;
    if (familyId) {
      queryClient.invalidateQueries({ queryKey: ["contactPreferences", "familyLast", contactIdToInvalidate] });
    } else {
      queryClient.invalidateQueries({ queryKey: ["contactPreferences", "last", contactIdToInvalidate] });
    }
  } catch (error) {
    console.error("Error saving education fund preference:", error);
  }
};

const handlePensionPreferenceChange = async (newData: PreferenceDistributionData) => {
  setPensionPreferenceData(newData);

  try {
    const prefs = familyId ? familyPrefs : contactPrefs;
    const existingPref = prefs?.find(p => p.preference_type === ContactPreferenceType.PENSION_DISTRIBUTION);

    if (existingPref) {
      await updatePreference.mutateAsync({
        id: existingPref.id,
        fields: { data: newData, reported_period: selectedPeriod }
      });
    } else {
      await createPreference.mutateAsync(
        familyId
          ? { family_id: familyId, contact_id: null, preference_type: ContactPreferenceType.PENSION_DISTRIBUTION, is_family: true, reported_period: selectedPeriod, data: newData }
          : { contact_id: id, family_id: null, preference_type: ContactPreferenceType.PENSION_DISTRIBUTION, is_family: false, reported_period: selectedPeriod, data: newData }
      );
    }

    const contactIdToInvalidate = familyId || id;
    if (familyId) {
      queryClient.invalidateQueries({ queryKey: ["contactPreferences", "familyLast", contactIdToInvalidate] });
    } else {
      queryClient.invalidateQueries({ queryKey: ["contactPreferences", "last", contactIdToInvalidate] });
    }
  } catch (error) {
    console.error("Error saving pension preference:", error);
  }
};

const handlerChanges = useMemo(() => {
    return modifiedRows.filter((row) => {
      const original = originalRows.find((o) => o.policy_number === row.policy_number);
      return original && original.handler_code !== row.handler_code;
    });
  }, [modifiedRows, originalRows]);

const handleAddHandler = () => {
  if (!newHandlerCode) return;
   setHandlersSet(prev => {
    const next = new Set(prev);
    next.add(newHandlerCode); // ייכנס לסוף -> שומר סדר
    return next;
  });
  setNewHandlerCode("");
};
useEffect(() => {
  setModifiedRows((prevRows) =>
    prevRows.map((row) => {
      const updatedFee = newFeesMap[row.policy_number];
      if (updatedFee === undefined || updatedFee === null) return row;
      return { ...row, saving_fee: updatedFee };
    })
  );
}, [newFeesMap, selectedSimulation]);

const handleNewFeesUpdated = (fees: Record<string, number | null>) => {
  setNewFeesMap((prev) => ({ ...prev, ...fees }));
};

const handleItemMoved = (item: MimshakData, toHandler: string) => {
    setModifiedRows((prevRows) => {
      // Use the current row from prevRows as the base — it carries fund/path changes made since last render
      const currentRow = prevRows.find((r) => r.policy_number === item.policy_number) ?? item;
      const original = originalRows.find((r) => r.policy_number === item.policy_number);
      const isReturningToOriginal = original && toHandler === original.handler_code;
      const movedItem: MimshakData = isReturningToOriginal
        ? { ...currentRow, handler_code: toHandler, fund_ids: original.fund_ids, data: original.data }
        : { ...currentRow, handler_code: toHandler };
      return prevRows.map((row) =>
        row.policy_number === item.policy_number ? movedItem : row
      );
    });
  };
  // const handleItemMoved = (item: MimshakData, toHandler: string) => {
  //   const originalItem = originalRows.find(r => r.policy_number === item.policy_number);
  //   const originalHandler = originalItem?.handler_code || item.handler_code;

  //   setModifiedRows((prevRows) => {
  //     // מוחקים רשומות ניוד קודמות של אותה פוליסה
  //     const filteredRows = prevRows.filter(
  //       row => !(row.policy_number === `${item.policy_number}_transfer_out`)
  //     );

  //     // אם זה חזרה ליצרן המקורי - מוחקים את נתוני הניוד
  //     if (toHandler === originalHandler) {
  //       return filteredRows.map((row) => {
  //         if (row.policy_number === item.policy_number) {
  //           return {
  //             ...row,
  //             handler_code: toHandler,
  //             transfer_out: originalItem?.transfer_out || null,
  //             transfer_out_to: originalItem?.transfer_out_to || null,
  //             transfer_in: originalItem?.transfer_in || null,
  //           };
  //         }
  //         return row;
  //       });
  //     }

  //     // אם זה ניוד ליצרן חדש - יוצרים שני רשומות:
  //     const transferAmount = originalItem?.funds_sum || item.funds_sum;

  //     const updatedRows = filteredRows.map((row) => {
  //       if (row.policy_number === item.policy_number) {
  //         // רשומה ביצרן החדש עם transfer_in
  //         return {
  //           ...row,
  //           handler_code: toHandler,
  //           transfer_in: transferAmount,
  //           transfer_out: null,
  //           transfer_out_to: null,
  //         };
  //       }
  //       return row;
  //     });

  //     // הוספת רשומת ניוד יוצא מהיצרן המקורי
  //     const transferOutRecord: MimshakData = {
  //       ...originalItem!,
  //       policy_number: `${item.policy_number}_transfer_out`, // מזהה ייחודי
  //       handler_code: originalHandler,
  //       transfer_out: transferAmount,
  //       transfer_out_to: toHandler,
  //       transfer_in: null,
  //       funds_sum: 0, // אפס כי זה כבר יצא
  //     };

  //     return [...updatedRows, transferOutRecord];
  //   });
  // };


const handleFundsSelected = (policyNumber: string, selections: PathFundSelection[]) => {
  setModifiedRows((prev) => prev.map((row) => {
    if (row.policy_number !== policyNumber) return row;
    const base = mergeDuplicateInvestmentPathsByPathCode({ ...row, data: JSON.parse(JSON.stringify(row.data)) });
    if (base.data?.products) {
      replacePathsInProduct(base, policyNumber, selections);
    }
    return base;
  }));

  const firstFund = selections[0]?.fund;
  if (firstFund?.managing_corporation_legal_id) {
    const currentRow = modifiedRows.find(r => r.policy_number === policyNumber);
    const newHandler = String(firstFund.managing_corporation_legal_id);
    if (currentRow && String(currentRow.handler_code) !== newHandler) {
      handleItemMoved(currentRow, newHandler);
    }
  }
};

const handleCubeClick = (mimshakRow: MimshakData, handlerCode?: string) => {
  setScatterDrawerPolicyNumber(mimshakRow.policy_number);
};

const handleAddFundSum = () => {
  if (newFundSum !== null && newFundSum > 0) {
    setFundSumChips([...fundSumChips, { amount: newFundSum, type: selectedAdditionType }]);
    setNewFundSum(null);
  }
};

const handleAdditionTypeSelect = (type: FundAdditionType) => {
  setSelectedAdditionType(type);
  setAdditionMenuAnchor(null);
};

const applyExtraToModifiedRow = (policyNumber: string, extra: number) => {
  setModifiedRows(prev => prev.map(row => {
    if (row.policy_number !== policyNumber) return row;
    const originalRow = originalRows.find(r => r.policy_number === policyNumber);
    const origFundsSum = originalRow?.funds_sum ?? (row as MimshakData & { _baseFundsSum?: number })._baseFundsSum ?? row.funds_sum ?? 0;
    const newFundsSum = origFundsSum + extra;
    const updatedRow: MimshakData = { ...row, funds_sum: newFundsSum, data: JSON.parse(JSON.stringify(row.data)) };

    // Update accumulated_savings CAPITAL entry
    const policies = updatedRow.data?.products?.flatMap(p => p.policies_or_accounts) ?? [];
    for (const policy of policies) {
      const origPolicy = originalRow?.data?.products?.flatMap(p => p.policies_or_accounts)
        ?.find(p => p.policy_number === policy.policy_number);

      // Scale accumulated_savings proportionally
      if (policy.accumulated_savings) {
        const origSavings = origPolicy?.accumulated_savings ?? policy.accumulated_savings;
        const origTotal = origSavings.reduce((s, e) => s + (e.total_savings ?? 0), 0);
        const scale = origTotal > 0 ? newFundsSum / origTotal : 1;
        policy.accumulated_savings = policy.accumulated_savings.map((entry, i) => ({
          ...entry,
          total_savings: (origSavings[i]?.total_savings ?? entry.total_savings ?? 0) * scale,
        }));
      }

      // Scale investment_paths proportionally
      if (policy.investment_paths && policy.investment_paths.length > 0) {
        // Scale by the current paths' own distribution — they may have been replaced/merged
        // by a path change, so they can't be matched by index to the original paths.
        const currentPaths = policy.investment_paths;
        const currentPathsTotal = currentPaths.reduce((s, p) => s + (p.accumulated_amount ?? 0), 0);
        policy.investment_paths = currentPaths.map((path) => ({
          ...path,
          accumulated_amount: currentPathsTotal > 0
            ? (path.accumulated_amount ?? 0) / currentPathsTotal * newFundsSum
            : newFundsSum / currentPaths.length,
        }));
      }
    }

    return updatedRow;
  }));
};

const handleFundSumDrop = (fromPolicy: string, toPolicy: string, droppedValue: number, dropType: FundAdditionType) => {
  // console.log("🎯 handleFundSumDrop called:", { fromPolicy, toPolicy, droppedValue, dropType });
  // console.log("  → Current fundSumDropMap before update:", fundSumDropMap);

  // recurrent_deposit and deposit_amount are written to modifiedRows only at save time (from fundSumDropMap)

  // כל הסכומים (deposit, recurrent, transfer) נכנסים ל-fundSumDropMap
  // console.log("  → Updating fundSumDropMap");
  if (fromPolicy && fromPolicy === toPolicy) return;

  const newMap = { ...fundSumDropMap };

  // Clear amount from source policy
  if (fromPolicy && newMap[fromPolicy] !== undefined) {
    const fromAdditions = { ...newMap[fromPolicy] };
    if (dropType === 'deposit') delete fromAdditions.deposit;
    else if (dropType === 'recurrent') delete fromAdditions.recurrent;
    else if (dropType === 'transfer') delete fromAdditions.transfer;
    else if (dropType === 'withdrawal') delete fromAdditions.withdrawal;
    if (!fromAdditions.deposit && !fromAdditions.recurrent && !fromAdditions.transfer && !fromAdditions.withdrawal) {
      delete newMap[fromPolicy];
    } else {
      newMap[fromPolicy] = fromAdditions;
    }
  }

  // Set amount on target policy
  const shouldAdd = fromPolicy && fromPolicy !== "";
  const prevTarget = newMap[toPolicy] ?? {};
  if (dropType === 'deposit') {
    newMap[toPolicy] = { ...prevTarget, deposit: shouldAdd ? (prevTarget.deposit ?? 0) + droppedValue : droppedValue };
  } else if (dropType === 'recurrent') {
    newMap[toPolicy] = { ...prevTarget, recurrent: shouldAdd ? (prevTarget.recurrent ?? 0) + droppedValue : droppedValue };
  } else if (dropType === 'transfer') {
    newMap[toPolicy] = { ...prevTarget, transfer: shouldAdd ? (prevTarget.transfer ?? 0) + droppedValue : droppedValue };
  } else if (dropType === 'withdrawal') {
    newMap[toPolicy] = { ...prevTarget, withdrawal: shouldAdd ? (prevTarget.withdrawal ?? 0) + droppedValue : droppedValue };
  }

  setFundSumDropMap(newMap);

  // Sync modifiedRows for affected policies
  if (fromPolicy) applyExtraToModifiedRow(fromPolicy, ChangesDataUtils.getTotalFundAddition(newMap[fromPolicy]));
  applyExtraToModifiedRow(toPolicy, ChangesDataUtils.getTotalFundAddition(newMap[toPolicy]));
};

  const handleDragEnd = (result: DropResult) => {
      // console.log("drag end result", result);
    const { source, destination, draggableId, type } = result;
    if (!destination) return;

    const from = source.droppableId;
    const to = destination.droppableId;

      // Handle manual asset chip drag to newPolicy
      if (draggableId.startsWith("manualAsset-") && to.startsWith("newPolicy-")) {
        const match = draggableId.match(/^manualAsset-(\d+)-(\d+)-([\d.]+)-idx-(\d+)$/);
        if (match) {
          const productType = parseInt(match[2]);
          const funds_sum = parseFloat(match[3]);
          const chipIndex = parseInt(match[4]);
          const handlerCode = to.replace("newPolicy-", "");
          const newMimshak = handleCreateNewMimshak(handlerCode, funds_sum, 'deposit', productType, selectedPeriod);
          setNewProductDrawer(newMimshak);
          setManualAssetChips((prev) => prev.filter((_, i) => i !== chipIndex));
        }
        return;
      }

      if (result.type === "fundSum") {
        let fundSum: number | null = null;
        let fundType: FundAdditionType = 'deposit';
        let fromPolicy: string | null = null;

        // Match patterns:
        // fundSum-amount-type (from chips area)
        // fundSum-amount-type-from-policyNumber (from existing policy)
        let chipContactId: number | null = null;
        let chipProductType: number | null = null;

        const matchWithTypeAndPolicy = result.draggableId.match(/^fundSum-(\d+(\.\d+)?)-(\w+)-from-(.+?)(?:-cid-(\d+))?(?:-pt-(\d+))?$/);
        const matchWithType = result.draggableId.match(/^fundSum-(\d+(\.\d+)?)-(\w+)(?:-cid-(\d+))?(?:-pt-(\d+))?$/);
        const matchFull = result.draggableId.match(/^fundSum-(\d+(\.\d+)?)-from-(.+)$/);
        const matchSimple = result.draggableId.match(/^fundSum-(\d+(\.\d+)?)/);

        if (matchWithTypeAndPolicy) {
          fundSum = parseFloat(matchWithTypeAndPolicy[1]);
          fundType = matchWithTypeAndPolicy[3] as FundAdditionType;
          fromPolicy = matchWithTypeAndPolicy[4];
          if (matchWithTypeAndPolicy[5]) chipContactId = parseInt(matchWithTypeAndPolicy[5]);
          if (matchWithTypeAndPolicy[6]) chipProductType = parseInt(matchWithTypeAndPolicy[6]);
        } else if (matchWithType) {
          fundSum = parseFloat(matchWithType[1]);
          fundType = matchWithType[3] as FundAdditionType;
          if (matchWithType[4]) chipContactId = parseInt(matchWithType[4]);
          if (matchWithType[5]) chipProductType = parseInt(matchWithType[5]);
        } else if (matchFull) {
          fundSum = parseFloat(matchFull[1]);
          fromPolicy = matchFull[3];
        } else if (matchSimple) {
          fundSum = parseFloat(matchSimple[1]);
        }
        const destinationId = result.destination?.droppableId;
         if (!fundSum || !destinationId) return;

        // Check if dragged back to chips area - remove from policy
        if (destinationId === "funds-chips" && fromPolicy) {
          // Reset the appropriate field in modifiedRows
          setModifiedRows((prev) =>
            prev.map((row) => {
              if (row.policy_number === fromPolicy) {
                if (fundType === 'recurrent') {
                  const originalRow = originalRows.find(r => r.policy_number === fromPolicy);
                  return { ...row, recurrent_deposit: originalRow?.recurrent_deposit ?? 0 };
                } else if (fundType === 'deposit') {
                  return { ...row, deposit_amount: 0 };
                }
              }
              return row;
            })
          );

          // Remove specific type from fundSumDropMap and sync modifiedRows
          const newMap = { ...fundSumDropMap };
          if (newMap[fromPolicy]) {
            const updated = { ...newMap[fromPolicy] };
            if (fundType === 'deposit') delete updated.deposit;
            else if (fundType === 'recurrent') delete updated.recurrent;
            else if (fundType === 'transfer') delete updated.transfer;
            if (!updated.deposit && !updated.recurrent && !updated.transfer) {
              delete newMap[fromPolicy];
            } else {
              newMap[fromPolicy] = updated;
            }
          }
          setFundSumDropMap(newMap);
          applyExtraToModifiedRow(fromPolicy, ChangesDataUtils.getTotalFundAddition(newMap[fromPolicy]));
          return;
        }

        const toPolicy = result.destination?.droppableId.replace("policy-", "");
       if (result.destination?.droppableId.startsWith("newPolicy-")) {
        const handlerCode = result.destination.droppableId.replace("newPolicy-", "");
        if (fundSum) {
          const sourceRow = fromPolicy
            ? (modifiedRows.find(r => r.policy_number === fromPolicy) ?? manualAssetChips.find(r => r.policy_number === fromPolicy) ?? null)
            : null;
          const isInsuranceOrPensionSource = sourceRow &&
            (sourceRow.adjusted_product_type === AdjustedProductType.ACTIVE_MANAGERS_INSURANCE ||
             sourceRow.adjusted_product_type === AdjustedProductType.INACTIVE_MANAGERS_INSURANCE ||
             sourceRow.adjusted_product_type === AdjustedProductType.MANDATORY_PENSION_FUND ||
             sourceRow.adjusted_product_type === AdjustedProductType.SUPPLEMENTARY_PENSION_FUND);
          const resolvedProductType = chipProductType ?? (isInsuranceOrPensionSource ? ProductType.PROVIDENT_FUND : null);
          const newMimshak = handleCreateNewMimshak(handlerCode, fundSum, fundType, resolvedProductType, selectedPeriod, sourceRow, chipContactId);
          if (fundType === 'recurrent') {
            setFundSumDropMap(prev => ({
              ...prev,
              [newMimshak.policy_number]: { ...prev[newMimshak.policy_number], recurrent: fundSum },
            }));
          }
          setNewProductDrawer(newMimshak);
        }
        return;
      }
        else if (fundSum && toPolicy) {
          handleFundSumDrop(fromPolicy ?? "", toPolicy, fundSum, fundType);
       }
        return;
      }
    const item = groupedWithNew[from]?.find((r) => r.policy_number === draggableId);
    if (!item) return;
    if (from === to && source.index === destination.index) return;

    // Compute new sortOrder using fractional indexing between the destination neighbours
    const getOrder = (policyNumber: string) => sortOrderMap[policyNumber] ?? Infinity;
    const destList = (groupedWithNew[to] ?? [])
      .filter((r) => r.policy_number !== draggableId)
      .sort((a, b) => getOrder(a.policy_number) - getOrder(b.policy_number));
    const insertAt = destination.index;
    const prevOrder = destList[insertAt - 1] ? getOrder(destList[insertAt - 1].policy_number) : (destList.length > 0 ? getOrder(destList[0].policy_number) - 1000 : 0);
    const nextOrder = destList[insertAt] ? getOrder(destList[insertAt].policy_number) : (destList.length > 0 ? getOrder(destList[destList.length - 1].policy_number) + 1000 : 1000);
    const newSortOrder = (prevOrder + nextOrder) / 2;

    setSortOrderMap((prev) => ({ ...prev, [draggableId]: newSortOrder }));
    handleItemMoved(item, to);
  };
const {
  handleCreateNewMimshak,
  createSplitMimshak,
  handleUpdateNewProduct,
  handleDeleteNewProduct,
  handleManagersInsuranceAssetsAdded,
} = useNewProductHandlers({
  contactId: contacts[0]?.id || 0,
  originalData: data,
  modifiedRows,
  setModifiedRows,
  setManualAssetChips,
  excludedOriginalRows,
  setExcludedModifiedRows,
});

const handleSplitProduct = (sourceMimshak: MimshakData, splitAmounts: number[]) => {
  const [firstAmount, ...restAmounts] = splitAmounts;
  const totalTransferOut = restAmounts.reduce((s, a) => s + a, 0);

  // Update source product with correct funds_sum, scaled internal data, and transfer_out
  setModifiedRows((prev) =>
    prev.map((row) => {
      if (row.policy_number !== sourceMimshak.policy_number) return row;
      const updated = applyFundsSumToMimshak(cloneDeep(row), firstAmount);
      updated.transfer_out = totalTransferOut;
      updated.transfer_out_to = sourceMimshak.handler_code;
      return updated;
    })
  );

  // Create a new transfered- product for each remaining split amount
  let lastCreated: MimshakData | null = null;
  restAmounts.forEach((amount) => {
    lastCreated = createSplitMimshak(sourceMimshak, amount);
  });
  if (lastCreated) setNewProductDrawer(lastCreated);
};

const handleNewProductDepositChange = (policyNumber: string, amount: number) => {
  if (amount > 0) {
    handleFundSumDrop("", policyNumber, amount, 'deposit');
  } else {
    const newMap = { ...fundSumDropMap };
    if (newMap[policyNumber]) {
      const updated = { ...newMap[policyNumber] };
      delete updated.deposit;
      if (!updated.deposit && !updated.recurrent && !updated.transfer && !updated.withdrawal) delete newMap[policyNumber];
      else newMap[policyNumber] = updated;
    }
    setFundSumDropMap(newMap);
    applyExtraToModifiedRow(policyNumber, ChangesDataUtils.getTotalFundAddition(newMap[policyNumber]));
    setModifiedRows(prev => prev.map(r => r.policy_number === policyNumber ? { ...r, deposit_amount: 0 } : r));
  }
};

const handleNewProductRecurrentChange = (policyNumber: string, amount: number | null) => {
  const originalRow = originalRows.find(r => r.policy_number === policyNumber);
  const originalRecurrent = originalRow?.recurrent_deposit ?? 0;
  const newMap = { ...fundSumDropMap };
  if (amount === null && originalRecurrent > 0) {
    newMap[policyNumber] = { ...(newMap[policyNumber] ?? {}), recurrent: null };
    setFundSumDropMap(newMap);
    setModifiedRows(prev => prev.map(r => r.policy_number === policyNumber ? { ...r, recurrent_deposit: 0 } : r));
  } else if (amount > 0) {
    newMap[policyNumber] = { ...(newMap[policyNumber] ?? {}), recurrent: amount };
    setFundSumDropMap(newMap);
    setModifiedRows(prev => prev.map(r => r.policy_number === policyNumber ? { ...r, recurrent_deposit: amount } : r));
  } else {
    // amount === 0: clear recurrent entirely (no original to stop)
    if (newMap[policyNumber]) {
      const updated = { ...newMap[policyNumber] };
      delete updated.recurrent;
      if (!updated.deposit && !updated.transfer && !updated.withdrawal) delete newMap[policyNumber];
      else newMap[policyNumber] = updated;
    }
    setFundSumDropMap(newMap);
    setModifiedRows(prev => prev.map(r => r.policy_number === policyNumber ? { ...r, recurrent_deposit: 0 } : r));
  }
};


  if (!userId) return <Typography color="error">אין משתמש מחובר</Typography>;
  if (loading || !overrides ) return <CircularProgress />;

  return (
    <>
     
<Grid item xs={12} md={3}>
            <Box display="flex" justifyContent="right" alignItems="center" height="100%" gap={1}>
              <Button variant="contained" onClick={() => setShowDiff(true)}>הצג השוואה</Button>
              <SimulationBar
              contacts={contacts}
              familyId={familyId}
              selectedPeriod={selectedPeriod}
              selectedPeriodType={selectedPeriodType}
              selectedSimulation={selectedSimulation}
              onSelect={(sim) => { handleLoadSimulationItems(sim); }}
              onExit={handleExitSimulationMode}
              drawerOpen={simulationDrawerOpen}
              onDrawerOpen={() => setSimulationDrawerOpen(true)}
              onDrawerClose={() => setSimulationDrawerOpen(false)}
              onCompare={handleCompareSimulation}
            />
        <Button variant="outlined" onClick={() => setPdfDialogOpen(true)}>תצוגת PDF</Button>
        <Button variant="outlined" onClick={() => setHtmlDialogOpen(true)}>
          תצוגת דוח HTML
        </Button>
        {/* <Button variant="outlined" onClick={() => setWordDialogOpen(true)}>
            תצוגת דוח Word
         </Button> */}
          <Button variant="outlined" startIcon={<BarChart sx={{ ml: 1 }} />} onClick={() => setDrawerOpen(true)} sx={{ ml: 2 }}>
            גרף דמי ניהול
          </Button>
            </Box>
          </Grid>
      <DragDropContext onDragEnd={handleDragEnd}>
              <Grid container spacing={2} mb={6}>
        <Grid item xs={12} md={isModified ? 6 : 10}>
          <Box sx={{ maxWidth: '500px', margin: '0 auto' }}>
            <Typography align="center">מצב מקורי</Typography>
            <div id="original-donut">
              <MimshakPieChart data={originalRows} storageKey="original-donut" />
            </div>
          </Box>
        </Grid>
         {isModified && (
            <Grid item xs={12} md={6} sx={{backgroundColor: '#fffbe6',}}>
              <Box sx={{ maxWidth: '500px', margin: '0 auto' }}>
                <Typography align="center">מצב לאחר שינוי</Typography>
                <div id="modified-donut">
                  <MimshakPieChart
                    data={modifiedRows}
                    storageKey="modified-donut"
                    fundSumDropMap={fundSumDropMap}
                  />
                </div>
              </Box>
            </Grid>
          )}

        </Grid>

          <Box sx={{ mt: 2, borderRight: '10px solid', borderRightColor: 'secondary.main', borderRadius: '0 8px 8px 0', boxShadow: '4px 0 12px -4px rgba(0,0,0,0.15)' }}>
          <Box sx={{ backgroundColor: '#f5f5f5', borderRadius: '8px 8px 0 0', px: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Tabs value={insightsTab} onChange={(_, v) => setInsightsTab(v)}>
              <Tab label={hasFutureAssets ? "הכל" : "הון"} />
              {hasFutureAssets && <Tab label="הון" />}
              {hasFutureAssets && <Tab label="כספי פנסיה גמל" />}
              {hasEducationFunds && <Tab label="קרנות השתלמות" />}
              <Tab label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  מעבדת חשיפה
                  {Object.keys(newExposureMap).length > 0 && (
                    <Box sx={{
                      backgroundColor: "#7B1FA2",
                      color: "#fff",
                      borderRadius: "50%",
                      minWidth: 20,
                      height: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      px: 0.5,
                    }}>
                      {Object.keys(newExposureMap).length}
                    </Box>
                  )}
                </Box>
              } />
              <Tab label="מעבדת דירוג תשואות" />
              <Tab label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    סיכום שינויים
                  {changesCount > 0 && (
                    <Box sx={{
                      backgroundColor: "secondary.main",
                      color: "#fff",
                      borderRadius: "50%",
                      minWidth: 20,
                      height: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      px: 0.5,
                    }}>
                      {changesCount}
                    </Box>
                  )}
                
                </Box>
              } />
            </Tabs>
          </Box>


          {insightsTab === 0 && (
            <Box sx={{ backgroundColor: '#f5f5f5', borderRadius: '0 0 8px 8px', p: 2, mb: 2 }}>
          <Grid container spacing={2} mt={2} mb={2}>
           <Grid item xs={12} md={isModified ? 6 : 10}>
            <Typography align="center">מצב מקורי</Typography>
            <ExposureGaugesByMimshakDataInteractive
              data={originalRows}
              preferenceData={hasFutureAssets ? undefined : capitalPreferenceData}
              onPreferenceChange={hasFutureAssets ? undefined : handlePreferenceChange}
            />
          </Grid>
          {isModified && (
            <Grid item xs={12} md={6} sx={{backgroundColor: '#fffbe6',}}>
              <Typography align="center">מצב לאחר שינוי</Typography>
              <ExposureGaugesByMimshakDataInteractive
                data={modifiedRows}
                originalData={originalRows}
                preferenceData={hasFutureAssets ? undefined : capitalPreferenceData}
                onPreferenceChange={hasFutureAssets ? undefined : handlePreferenceChange}
              />
            </Grid>
          )}
          </Grid>

             <Grid container spacing={2} mt={2} mb={2}>
           <Grid item xs={12} md={isModified ? 6 : 10}>
             <div id="original-yields">
            <WeightedProductYieldsSummary
                title={hasFutureAssets ? "תשואה שנתית משוקללת - הכל (לפני שינוי)" : "תשואה שנתית משוקללת - הון (לפני שינוי)"}
                data={originalRows}
              />
              </div>
          </Grid>
          {isModified && (
            <Grid item xs={12} md={6} sx={{backgroundColor: '#fffbe6',}}>
               <div id="modified-yields">
              <WeightedProductYieldsSummary
                title={hasFutureAssets ? "תשואה שנתית משוקללת - הכל (לאחר שינוי)" : "תשואה שנתית משוקללת - הון (לאחר שינוי)"}
                data={modifiedRows}
                originalData={originalRows}
                fundSumDropMap={fundSumDropMap}
              />
              </div>
            </Grid>
          )}
          </Grid>

            </Box>
          )}

          {insightsTab === capitalTabIndex && hasFutureAssets && (
            <Box sx={{ backgroundColor: '#f5f5f5', borderRadius: '0 0 8px 8px', p: 2, mb: 2 }}>
              <Grid container spacing={2} mt={2} mb={2}>
                <Grid item xs={12} md={isModified ? 6 : 10}>
                  <Typography align="center">מצב מקורי</Typography>
                  <ExposureGaugesByMimshakDataInteractive
                    data={originalCapitalRows}
                    preferenceData={capitalPreferenceData}
                    onPreferenceChange={handlePreferenceChange}
                  />
                </Grid>
                {isModified && (
                  <Grid item xs={12} md={6} sx={{ backgroundColor: '#fffbe6' }}>
                    <Typography align="center">מצב לאחר שינוי</Typography>
                    <ExposureGaugesByMimshakDataInteractive
                      data={modifiedCapitalRows}
                      originalData={originalCapitalRows}
                      preferenceData={capitalPreferenceData}
                      onPreferenceChange={handlePreferenceChange}
                    />
                  </Grid>
                )}
              </Grid>
              <Grid container spacing={2} mt={2} mb={2}>
                <Grid item xs={12} md={isModified ? 6 : 10}>
                  <WeightedProductYieldsSummary
                    title="תשואה שנתית משוקללת - הון (לפני שינוי)"
                    data={originalCapitalRows}
                  />
                </Grid>
                {isModified && (
                  <Grid item xs={12} md={6} sx={{ backgroundColor: '#fffbe6' }}>
                    <WeightedProductYieldsSummary
                      title="תשואה שנתית משוקללת - הון (לאחר שינוי)"
                      data={modifiedCapitalRows}
                      originalData={originalCapitalRows}
                      fundSumDropMap={fundSumDropMap}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {insightsTab === futureAssetsTabIndex && hasFutureAssets && (
            <Box sx={{ backgroundColor: '#f5f5f5', borderRadius: '0 0 8px 8px', p: 2, mb: 2 }}>
              <Grid container spacing={2} mt={2} mb={2}>
                <Grid item xs={12} md={isModified ? 6 : 10}>
                  <Typography align="center">מצב מקורי</Typography>
                  <ExposureGaugesByMimshakDataInteractive
                    data={originalProvidentRows}
                    preferenceData={pensionPreferenceData}
                    onPreferenceChange={handlePensionPreferenceChange}
                  />
                </Grid>
                {isModified && (
                  <Grid item xs={12} md={6} sx={{ backgroundColor: '#fffbe6' }}>
                    <Typography align="center">מצב לאחר שינוי</Typography>
                    <ExposureGaugesByMimshakDataInteractive
                      data={modifiedProvidentRows}
                      originalData={originalProvidentRows}
                      preferenceData={pensionPreferenceData}
                      onPreferenceChange={handlePensionPreferenceChange}
                    />
                  </Grid>
                )}
              </Grid>
              <Grid container spacing={2} mt={2} mb={2}>
                <Grid item xs={12} md={isModified ? 6 : 10}>
                  <WeightedProductYieldsSummary
                    title="תשואה שנתית משוקללת — כספים פנסיה גמל (לפני שינוי)"
                    data={originalProvidentRows}
                  />
                </Grid>
                {isModified && (
                  <Grid item xs={12} md={6} sx={{ backgroundColor: '#fffbe6' }}>
                    <WeightedProductYieldsSummary
                      title="תשואה שנתית משוקללת — כספים פנסיה גמל (לאחר שינוי)"
                      data={modifiedProvidentRows}
                      originalData={originalProvidentRows}
                      fundSumDropMap={fundSumDropMap}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {insightsTab === educationTabIndex && hasEducationFunds && (
            <Box sx={{ backgroundColor: '#f5f5f5', borderRadius: '0 0 8px 8px', p: 2, mb: 2 }}>
          <Grid container spacing={2} mt={2} mb={2}>
           <Grid item xs={12} md={isModified ? 6 : 10}>
            <Typography align="center">מצב מקורי</Typography>
            <ExposureGaugesByMimshakDataInteractive
              data={originalEducationRows}
              preferenceData={educationFundPreferenceData}
              onPreferenceChange={handleEducationPreferenceChange}
            />
          </Grid>
          {isModified && (
            <Grid item xs={12} md={6} sx={{backgroundColor: '#fffbe6',}}>
              <Typography align="center">מצב לאחר שינוי</Typography>
              <ExposureGaugesByMimshakDataInteractive
                data={modifiedEducationRows}
                originalData={originalEducationRows}
                preferenceData={educationFundPreferenceData}
                onPreferenceChange={handleEducationPreferenceChange}
              />
            </Grid>
          )}
          </Grid>

             <Grid container spacing={2} mt={2} mb={2}>
           <Grid item xs={12} md={isModified ? 6 : 10}>
             <div id="original-education-yields">
            <WeightedProductYieldsSummary
                title="תשואה שנתית משוקללת - קרנות השתלמות (לפני שינוי)"
                data={originalEducationRows}
              />
              </div>
          </Grid>
          {isModified && (
            <Grid item xs={12} md={6} sx={{backgroundColor: '#fffbe6',}}>
               <div id="modified-education-yields">
              <WeightedProductYieldsSummary
                title="תשואה שנתית משוקללת - קרנות השתלמות (לאחר שינוי)"
                data={modifiedEducationRows}
                originalData={originalEducationRows}
                fundSumDropMap={fundSumDropMap}
              />
              </div>
            </Grid>
          )}
          </Grid>
            </Box>
          )}

          {insightsTab === scatterTabIndex && (
            <ExposureLabDnD
              modifiedRows={modifiedRows}
              originalRows={originalRows}
              isModified={isModified}
              onPathDrag={addDragChange}
              dragChanges={dragChanges}
              newExposureMap={newExposureMap}
              onRemovePolicy={removePolicyChanges}
              onOpenPathDrawer={setScatterDrawerPolicyNumber}
              preferenceData={capitalPreferenceData}
              onPreferenceChange={handlePreferenceChange}
              fundSumDropMap={fundSumDropMap}
            />
          )}

          {insightsTab === yieldRatingTabIndex && (
            <YieldLab
              originalRows={originalRows}
              modifiedRows={modifiedRows}
              fundSumDropMap={fundSumDropMap}
              isModified={isModified}
            />
          )}

          {insightsTab === changesTabIndex && (
            <Box sx={{ backgroundColor: '#f5f5f5', borderRadius: '0 0 8px 8px', p: 2, mb: 2 }}>
              <SummaryChangesVisual
                saveButtonMode="always"
                originalRows={originalRows}
                modifiedRows={modifiedRows}
                fundSumDropMap={fundSumDropMap}
                simulationItems={simulationItemsWithEdits}
                selectedPeriodType={selectedPeriodType}
                simulation={selectedSimulation}
                familyId={familyId}
                contactColorMap={contactColorMap}
                contacts={contacts}
                newExposureMap={newExposureMap}
                source="gemelLab"
                onNotesChange={(notes) => setEditedFields(prev => {
                  const next = { ...prev };
                  Object.entries(notes).forEach(([policy, note]) => {
                    next[policy] = { ...(next[policy] ?? {}), note };
                  });
                  return next;
                })}
                onItemsReplaced={(freshItems) => {
                  const noteMap: Record<string, string> = {};
                  freshItems.forEach(item => { if (item.note) noteMap[item.policy_number] = item.note; });
                  setLoadedNotes(noteMap);
                  setEditedFields({});
                }}
                onUpdateSimulation={(name, notes, status) => {
                  if (selectedSimulation) setSelectedSimulation({ ...selectedSimulation, name, notes, status });
                }}
                excludedSimulationItems={excludedSimulationItems}
                excludedOriginalRows={excludedOriginalRows}
                excludedModifiedRows={excludedModifiedRows}
              />
            </Box>
          )}
          </Box>


        <Grid container spacing={2} mb={6} mt={2}>
          <Grid item xs={12} md={isModified ? 6 : 10}>
            <WeightedPerformanceBar products={originalProducts} fundData={performanceFundData} />
          </Grid>
          {isModified && (
            <Grid item xs={12} md={6} sx={{ backgroundColor: '#fffbe6' }}>
              <WeightedPerformanceBar products={modifiedProducts} fundData={performanceFundData} />
            </Grid>
          )}
        </Grid>

        <Grid container spacing={2} mb={6} mt={2}>
          <Grid item xs={12} md={isModified ? 6 : 10}>
              <div id="fee-savings">
            <FeeSavingsComparison
            isModifiedSavingFee={isModifiedSavingFee}
            original={originalRows}
              modified={modifiedRows}
              fundSumDropMap={fundSumDropMap} />
              </div>
          </Grid>
          </Grid>
        
        <Grid container spacing={2} alignItems="center">
          {/* עמודה עם שדה וכפתור */}
          <Grid item xs={12} md="auto">
            <Box display="flex" flexDirection="row" alignItems="center" gap={1} minWidth={300}>
              <Box sx={{ minWidth: 250 }}>
                <HandlerAutocompleteField
                  value={newHandlerCode}
                  onChange={setNewHandlerCode}
                  placeholder="בחר יצרן להוספה..."
                  showIcons={true}
                />
              </Box>
              <Button
                variant="contained"
                startIcon={<Add sx={{ ml: 1 }} />}
                onClick={handleAddHandler}
                disabled={!newHandlerCode || handlersSet.has(newHandlerCode)}
              >
                הוסף יצרן
              </Button>
            </Box>
          </Grid>

          {/* עמודת סכום חדש + כפתור + Chips */}
          <Grid item xs={12} md>
            <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
              <CurrencyTextField
                variant="outlined"
                size="small"
                sx={{ width: 180 }}
                placeholder="הקלד סכום חדש"
                value={newFundSum}
                onChange={(value) => setNewFundSum(value)}
                onKeyDown={(e) => {
                  if ((e as React.KeyboardEvent).key === "Enter") {
                    e.preventDefault();
                    handleAddFundSum();
                  }
                }}
              />
              <ButtonGroup variant="contained">
              
                <Button
                  size="small"
                  onClick={(e) => setAdditionMenuAnchor(e.currentTarget)}
                  sx={{
                    backgroundColor: getAdditionTypeColor(selectedAdditionType),
                    color: 'white',
                    borderRadius: '0px',
                    borderLeft: '1px solid rgba(255, 255, 255, 0.5)',
                  }}
                >
                  <ArrowDropDown />
                </Button>
                  <Button
                  onClick={handleAddFundSum}
                  sx={{
                    color: 'white',
                    backgroundColor: getAdditionTypeColor(selectedAdditionType),
                    borderRadius: '0px'
                  }}
                >
                  הוסף {getAdditionTypeLabel(selectedAdditionType)}
                </Button>
              </ButtonGroup>
              <Menu
                anchorEl={additionMenuAnchor}
                dir="rtl"
                open={Boolean(additionMenuAnchor)}
                onClose={() => setAdditionMenuAnchor(null)}
              >
                <MenuItem onClick={() => handleAdditionTypeSelect('deposit')}>
                  {getAdditionTypeLabel('deposit')}
                </MenuItem>
                <MenuItem onClick={() => handleAdditionTypeSelect('recurrent')}>
                  {getAdditionTypeLabel('recurrent')}
                </MenuItem>
                <MenuItem onClick={() => handleAdditionTypeSelect('transfer')}>
                  {getAdditionTypeLabel('transfer')}
                </MenuItem>
                <MenuItem onClick={() => handleAdditionTypeSelect('withdrawal')}>
                  {getAdditionTypeLabel('withdrawal')}
                </MenuItem>
              </Menu>
              <Button
                variant="outlined"
                size="small"
                endIcon={<ArrowDropDown />}
                onClick={(e) => setActionsMenuAnchor(e.currentTarget)}
                sx={{ px: 2, py: 1 }}
              >
                פעולות נוספות
              </Button>
              <Menu
                anchorEl={actionsMenuAnchor}
                dir="rtl"
                open={Boolean(actionsMenuAnchor)}
                onClose={() => setActionsMenuAnchor(null)}
              >
                <MenuItem onClick={() => { setManualDialogOpen(true); setActionsMenuAnchor(null); }}>
                  הוסף מנכסים
                </MenuItem>
                <MenuItem onClick={() => { setExecutiveDialogOpen(true); setActionsMenuAnchor(null); }}>
                  הוסף מביטוח מנהלים / פנסיה לא פעילה
                </MenuItem>
                <MenuItem onClick={() => { setFillProvidentFundDrawerOpen(true); setActionsMenuAnchor(null); }}>
                  מלא גמל להשקעה עד תקרה
                </MenuItem>
              </Menu>
              <Droppable droppableId="funds-chips" direction="horizontal" type="fundSum">
                {(provided) => (
                  <Box display="flex" gap={1} flexWrap="wrap" ref={provided.innerRef} {...provided.droppableProps}>
                    {fundSumChips.map((chip, index) => {
                      const chipColor = chip.productType != null
                        ? colorProductType(chip.productType)
                        : getAdditionTypeColor(chip.type);
                      const contactBorderColor = chip.contactId != null ? contactColorMap[chip.contactId] : undefined;
                      const chipLabel = chip.type === 'deposit' ? '' :
                                       chip.type === 'recurrent' ? 'M' :
                                       chip.type === 'withdrawal' ? '' :
                                       'T';
                      const chipSuffix = chip.type === 'withdrawal' ? '-' : '+';
                      return (
                        <Draggable draggableId={`fundSum-${chip.amount}-${chip.type}${chip.contactId != null ? `-cid-${chip.contactId}` : ""}${chip.productType != null ? `-pt-${chip.productType}` : ""}`} index={index} key={index}>
                          {(provided) => (
                            <Chip
                              label={`${chipLabel}${formatNumberFloor(chip.amount)}${chipSuffix}`}
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...{ ...provided.dragHandleProps, tabIndex: -1 }}
                              onDelete={() => setFundSumChips((prev) => prev.filter((_, i) => i !== index))}
                              sx={{
                                backgroundColor: chipColor,
                                color: 'white',
                                fontWeight: 'bold',
                                ...(contactBorderColor && {
                                  borderRight: `8px solid ${contactBorderColor}`,
                                }),
                                '& .MuiChip-deleteIcon': {
                                  color: 'rgba(255,255,255,0.5)',
                                  '&:hover': { color: 'rgba(255,255,255,0.9)' },
                                },
                              }}
                            />
                          )}
                        </Draggable>
                      );
                    })}
                    <AssetChipsPanel
                      contacts={contacts}
                      selectedPeriod={selectedPeriod}
                      selectedPeriodType={selectedPeriodType}
                      data={data}
                      manualAssetChips={manualAssetChips}
                      onAssetsAdded={handleManagersInsuranceAssetsAdded}
                      onChipDelete={(index) => setManualAssetChips((prev) => prev.filter((_, i) => i !== index))}
                      fundSumChipsLength={fundSumChips.length}
                      contactColorMap={contactColorMap}
                      openManualDialog={manualDialogOpen}
                      onManualDialogClose={() => setManualDialogOpen(false)}
                      openExecutiveDialog={executiveDialogOpen}
                      onExecutiveDialogClose={() => setExecutiveDialogOpen(false)}
                    />
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
              {/* <Button
                variant="outlined"
                onClick={() => setTransferFlowDrawerOpen(true)}
                sx={{ marginLeft: 'auto' }}
              >
                ניודים
              </Button> */}
            </Box>
          </Grid>
        </Grid>
        <div id="fees-by-product">

        <Grid container spacing={2} mt={1}>
       {Array.from(handlersSet).map(handlerCode => {
           const rows = groupedWithNew[handlerCode] ?? [];
           const originalRowsMap = Object.fromEntries(originalRows.map(r => [r.policy_number, r]));
           const modifiedRowsMap = Object.fromEntries(modifiedRows.map(r => [r.policy_number, r]));

          return (
            <Grid item key={handlerCode} xs={12} md={4}>
              <FeesByProductDnD
                handlerCode={handlerCode}
                modified_rows={rows}
                originalRowsMap={originalRowsMap}
                modifiedRowsMap={modifiedRowsMap}
                overrides={overrides.filter((o) => o.handler_code === handlerCode)}
                onItemMoved={handleItemMoved}
                onNewFeesUpdated={handleNewFeesUpdated}
                agentProducerCodes={agentProducerCodes}
                fundSumDropMap={fundSumDropMap}
                selectedPeriodType={selectedPeriodType}
                setNewProductDrawer={setNewProductDrawer}
                contactColorMap={contactColorMap}
                onCubeClick={handleCubeClick}
                newExposureMap={newExposureMap}
                loadedManualFees={loadedManualFees}
                onSplitProduct={handleSplitProduct}
                onRecurrentChange={handleNewProductRecurrentChange}
                onDepositChange={handleNewProductDepositChange}
                sortOrderMap={sortOrderMap}
              />
            </Grid>
          );
          })}
        </Grid>
        </div>
      </DragDropContext>
    
      {/* {handlerChanges.length > 0 && (
        <Box mt={6}>
          <Typography variant="h6" gutterBottom>בחירת מסלול חדש</Typography>
          <Grid container spacing={2}>
            {handlerChanges.map((row) => (
              <Grid item xs={12} md={6} key={row.policy_number}>
                <PathSelection mimshak={row} onFundSelected={handleFundSelected} onOpenChartDrawer={handleCubeClick} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )} */}
<Suspense fallback={null}>
  <DiffDrawer
    showDiff={showDiff}
    setShowDiff={setShowDiff}
    originalRows={originalRows}
    modifiedRows={modifiedRows}
    agentAssignmentMap={agentAssignmentMap}
    onAgentAssignmentChange={setAgentAssignmentMap}
    selectedPeriodType={selectedPeriodType}
    fundSumDropMap={fundSumDropMap}
    simulationItems={simulationItemsWithEdits}
    onSimulationItemUpdate={handleSimulationItemUpdate}
    contacts={contacts}
    agentProducerCodes={agentProducerCodes}
    familyId={familyId}
    selectedSimulation={selectedSimulation}
    onUpdateSimulation={(name, notes, status) => {
      if (selectedSimulation) setSelectedSimulation({ ...selectedSimulation, name, notes, status });
    }}
    source="gemelLab"
  />
</Suspense>

      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)} disableScrollLock>
        <Box sx={{ width: 800, p: 2, mt: 10, direction: "rtl" }}>
          <CommissionComparisonChart overrides={overrides} />
          <Divider sx={{ my: 2 }} />
          <CommissionOverridesChart overrides={overrides} />
        </Box>
      </Drawer>

      {/* TransferFlowDiagram Drawer */}
      <Drawer anchor="left" open={transferFlowDrawerOpen} onClose={() => setTransferFlowDrawerOpen(false)} disableScrollLock>
        <Box sx={{ width: 1000, p: 2, mt: 10, direction: "rtl" }}>
          <Typography variant="h6" gutterBottom>
            תרשים ניודים {isModified ? '(כולל שינויים)' : ''}
          </Typography>
          <Divider sx={{ my: 2 }} />

          <TransferFlowDiagram data={modifiedRows} />
        </Box>
      </Drawer>

      <Suspense fallback={<div>טוען תצוגת דוח...</div>}>
{wordDialogOpen && (
<WordReportPreviewDialog
open={wordDialogOpen}
onClose={() => setWordDialogOpen(false)}
contacts={contacts}
originalRows={originalRows}
modifiedRows={modifiedRows}
dateGenerated={new Date().toLocaleDateString("he-IL")}
reportDate={selectedPeriod}
pieChartIds={{ original: "original-donut", modified: "modified-donut" }}
/>
)}
</Suspense>
{pdfDialogOpen && (
  <Suspense fallback={<div>טוען PDF...</div>}>
    <FeesLabPDFExport
      open={pdfDialogOpen}
      onClose={() => setPdfDialogOpen(false)}
      contacts={contacts}
      originalRows={originalRows}
      modifiedRows={modifiedRows}
      reportDate={selectedPeriod}
      dateGenerated={new Date().toLocaleDateString("he-IL")}
    />
  </Suspense>
)}
{htmlDialogOpen && (
  <Suspense fallback={<div>טוען עורך HTML...</div>}>
    <FeesLabWYSIWYGLauncher
      open={htmlDialogOpen}
      onClose={() => setHtmlDialogOpen(false)}
      contacts={contacts}
      originalRows={originalRows}
      modifiedRows={modifiedRows}
      reportDate={selectedPeriod}
      dateGenerated={new Date().toLocaleDateString("he-IL")}
      selectedPeriodType={selectedPeriodType}
      fundSumDropMap={fundSumDropMap}
      mimshakData={data}

    />
  </Suspense>
)}

{comparisonSimulation && (
  <SimulationComparisonDialog
    open={!!comparisonSimulation}
    simulation={comparisonSimulation.simulation}
    simulationData={comparisonSimulation.filteredData}
    currentData={filteredData} // ✅ הנתונים הנוכחיים שכבר יש
    contacts={contacts}
    onClose={() => setComparisonSimulation(null)}
  />
)}
     {newProductDrawer && (
  <NewProductTypeDrawer
    open={!!newProductDrawer}
    mimshak={newProductDrawer}
    contacts={contacts}
    onClose={() => setNewProductDrawer(null)}
    onUpdate={(updated) => {
      const fresh = modifiedRows.find((x) => x.policy_number === updated.policy_number);
      const merged = { ...fresh, ...updated };
      handleUpdateNewProduct(merged);
      setNewProductDrawer(merged);
    }}
    onDelete={(m) => {
      handleDeleteNewProduct(m.policy_number);
      setNewProductDrawer(null);
    }}
    onSplitProduct={(src, amounts) => {
      handleSplitProduct(src, amounts);
    }}
    currentDeposit={fundSumDropMap[newProductDrawer.policy_number]?.deposit ?? 0}
    currentRecurrent={fundSumDropMap[newProductDrawer.policy_number]?.recurrent ?? 0}
    onDepositChange={handleNewProductDepositChange}
    onRecurrentChange={handleNewProductRecurrentChange}
  />
)}


{scatterDrawerPolicyNumber && (() => {
  const mimshak = modifiedRows.find(r => r.policy_number === scatterDrawerPolicyNumber);
  if (!mimshak) return null;
  const originalMimshak = originalRows.find(r => r.policy_number === scatterDrawerPolicyNumber);
  const sourcePaths = ((originalMimshak ?? mimshak)?.data?.products ?? []).flatMap(p => MimshakProductUtil.getMergedPathsFromProduct(p));
  const drawerPaths = sourcePaths.map(p => ({
    pathCode: p.path_code,
    pathName: p.path_name ?? '',
    amount: p.accumulated_amount ?? 0,
    fund: fundService.getFundFromCacheById(p.path_code) ?? null,
  })).filter(p => p.fund);
  return (
    <SelectNewPathDrawer
      open={true}
      onClose={() => setScatterDrawerPolicyNumber(null)}
      mimshak={mimshak}
      originalMimshak={originalMimshak}
      title={`בחירת מסלול - פוליסה ${mimshak.policy_number}`}
      onFundsSelected={(selections) => {
        if (scatterDrawerPolicyNumber) {
          handleFundsSelected(scatterDrawerPolicyNumber, selections);
          setScatterDrawerPolicyNumber(null);
        }
      }}
      onReset={() => {
        if (!scatterDrawerPolicyNumber || !originalMimshak) return;
        setModifiedRows(prev => prev.map(row => {
          if (row.policy_number !== scatterDrawerPolicyNumber) return row;
          return resetOriginalInvestmentPaths(
            { ...row, data: JSON.parse(JSON.stringify(row.data)) },
            originalMimshak,
            scatterDrawerPolicyNumber
          );
        }));
        setScatterDrawerPolicyNumber(null);
      }}
      exposureChanges={newExposureMap[mimshak.policy_number]}
      preferenceData={capitalPreferenceData}
      paths={drawerPaths}
    />
  );
})()}

<Suspense fallback={null}>
  <FillInvestmentProvidentFundDrawer
    open={fillProvidentFundDrawerOpen}
    onClose={() => setFillProvidentFundDrawerOpen(false)}
    contacts={contacts}
    mimshakData={data}
    selectedPeriod={selectedPeriod}
    onAddChips={(chips) => setFundSumChips((prev) => [...prev, ...chips])}
  />
</Suspense>


    </>
  );
};

export default FeesLabPageDnD;